// src/components/TranslationEditor.js
import React, { useContext, useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { AppContext } from "../App"
import { saveTranslations } from "./translations"
import {
  flattenTranslations,
  unflattenTranslations,
} from "../utils/translationUtils"
import { useLocation } from "react-router-dom"
import { routesMap } from "../routesMap"
import { IoMdClose } from "react-icons/io"
import {
  FaSave,
  FaExclamationCircle,
  FaEdit,
  FaTimes,
  FaArrowLeft,
} from "react-icons/fa"

// Import our modular components
import {
  ModuleSelector,
  ProductManager,
  MenuManager,
  ImageManager,
  ListinoManager,
} from "./translation-modules"

const TranslationEditor = () => {
  const { i18n, ready } = useTranslation("global")
  const { isEditMode, setIsEditMode } = useContext(AppContext)
  const location = useLocation()

  // States for translations (current and secondary)
  const [translations, setTranslations] = useState({}) // e.g. "it"
  const [otherTranslations, setOtherTranslations] = useState({}) // e.g. "en"
  const [flatTranslations, setFlatTranslations] = useState({}) // for normal pages
  const [otherFlatTranslations, setOtherFlatTranslations] = useState({})
  const [initialFlatTranslations, setInitialFlatTranslations] = useState({})
  const [initialOtherFlatTranslations, setInitialOtherFlatTranslations] =
    useState({})

  // For menu pages, store the initial nested state so we can compare later
  const [initialMenuTranslations, setInitialMenuTranslations] = useState(null)
  const [initialOtherMenuTranslations, setInitialOtherMenuTranslations] =
    useState(null)

  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState(null)

  // UI toggle states
  const [toggleOtherKeys, setToggleOtherKeys] = useState({}) // for regular fields

  // Current module being edited
  const [currentModule, setCurrentModule] = useState("translation")

  // State to control the footer visibility for submodule forms
  const [showSubmoduleFooter, setShowSubmoduleFooter] = useState(false)
  const [submoduleAction, setSubmoduleAction] = useState(null)
  const [submoduleName, setSubmoduleName] = useState("")

  // Confirmation modal state for missing edits
  const [confirmMissing, setConfirmMissing] = useState(null)

  // Helper: Get current page key from the URL
  const getCurrentPage = () => {
    const path = location.pathname.toLowerCase()
    const pathToKey = {
      "/menu-carta": "menuCarta",
      "/wine-list": "wineList",
      "/aperitivo-menu": "menuAperitivo",
      "/gift-card": "giftCard",
      "/about": "aboutUs",
      "/celebrations": "celebrations",
      "/reviews": "reviews",
      "/privacy": "privacy",
      "/listino-prodotti": "listino", // Added for Italian
      "/en/product-catalog": "listino", // Added for English
      "/": "home",
    }

    for (const [basePath, localizedPaths] of Object.entries(routesMap)) {
      if (localizedPaths.it === path || localizedPaths.en === path) {
        return pathToKey[basePath] || basePath.replace("/", "")
      }
    }
    return "home"
  }

  // Helper: Returns the secondary language (assumes only "it" and "en")
  const getSecondaryLanguage = () => {
    return i18n.language === "en" ? "it" : "en"
  }

  useEffect(() => {
    if (!ready) return
    const currentLang = i18n.language
    const secondaryLang = getSecondaryLanguage()

    // Get resource bundles from i18next
    const currentTranslations =
      i18n.getResourceBundle(currentLang, "global") || {}
    let secondaryTranslations = {}
    try {
      secondaryTranslations =
        i18n.getResourceBundle(secondaryLang, "global") || {}
    } catch (e) {
      secondaryTranslations = {}
    }
    // If the secondary bundle is empty, fetch it from the server.
    if (Object.keys(secondaryTranslations).length === 0) {
      fetch(`https://salumidelnorcino.com/api.php?lang=${secondaryLang}`)
        .then((response) => response.json())
        .then((data) => {
          secondaryTranslations = data || {}
          setOtherTranslations(secondaryTranslations)
          const otherFlat = flattenTranslations(secondaryTranslations)
          setOtherFlatTranslations(otherFlat)
          setInitialOtherFlatTranslations(otherFlat)
        })
        .catch((err) =>
          console.error("Error loading secondary translations:", err)
        )
    } else {
      setOtherTranslations(secondaryTranslations)
      const otherFlat = flattenTranslations(secondaryTranslations)
      setOtherFlatTranslations(otherFlat)
      setInitialOtherFlatTranslations(otherFlat)
    }

    setTranslations(currentTranslations)
    const flatCurr = flattenTranslations(currentTranslations)
    setFlatTranslations(flatCurr)
    setInitialFlatTranslations(flatCurr)

    const currentPage = getCurrentPage()

    // For menu pages, store initial nested state
    if (currentTranslations[currentPage]?.sections) {
      setInitialMenuTranslations(
        JSON.parse(JSON.stringify(currentTranslations[currentPage].sections))
      )
    }

    if (
      otherTranslations[currentPage] &&
      otherTranslations[currentPage].sections
    ) {
      setInitialOtherMenuTranslations(
        JSON.parse(JSON.stringify(otherTranslations[currentPage].sections))
      )
    }

    // Auto-select the appropriate module based on the current page
    const pageType = getCurrentPage()
    if (pageType === "home") {
      setCurrentModule("products")
    } else if (["menuCarta", "wineList", "menuAperitivo"].includes(pageType)) {
      setCurrentModule("menu")
    } else if (pageType === "listino") {
      setCurrentModule("listino")
    }
  }, [i18n.language, i18n, ready, location.pathname])

  // --- REGULAR (Non-menu) EDITOR FUNCTIONS ---

  const toggleOtherKey = (key) => {
    setToggleOtherKeys((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleRegularChange = (key, value) => {
    setFlatTranslations((prev) => ({ ...prev, [key]: value }))
  }

  const handleOtherRegularChange = (key, value) => {
    setOtherFlatTranslations((prev) => ({ ...prev, [key]: value }))
  }

  // Handle updates from the translation modules
  const handleUpdateTranslations = (
    updatedCurrentTranslations,
    updatedOtherTranslations
  ) => {
    setTranslations(updatedCurrentTranslations)
    setOtherTranslations(updatedOtherTranslations)

    // Update flat translations as well for regular editor
    const flatCurr = flattenTranslations(updatedCurrentTranslations)
    setFlatTranslations(flatCurr)

    const flatOther = flattenTranslations(updatedOtherTranslations)
    setOtherFlatTranslations(flatOther)
  }

  // Register a submodule action for contextual footer
  const registerSubmoduleAction = (action, name) => {
    setSubmoduleAction(() => action)
    setSubmoduleName(name)
    setShowSubmoduleFooter(true)
  }

  // Clear submodule action
  const clearSubmoduleAction = () => {
    setShowSubmoduleFooter(false)
    setSubmoduleAction(null)
    setSubmoduleName("")
  }

  // SAVE FUNCTION
  const handleSave = async () => {
    setIsSaving(true)
    setMessage(null)
    const currentPage = getCurrentPage()
    const isMenuPage = ["menuCarta", "wineList", "menuAperitivo"].includes(
      currentPage
    )
    const isListinoPage = currentPage === "listino"

    if ((isMenuPage || isListinoPage) && currentModule === "translation") {
      // Check for menu item fields that were edited in current language
      // but the corresponding English field was not updated.
      let editedMenuFields = []
      if (initialMenuTranslations && initialOtherMenuTranslations) {
        const currentMenu = translations[currentPage]?.sections
        const initialMenu = initialMenuTranslations
        const currentOtherMenu = otherTranslations[currentPage]?.sections
        const initialOtherMenu = initialOtherMenuTranslations

        if (currentMenu) {
          for (const section in currentMenu) {
            if (currentMenu[section]?.items) {
              for (const itemKey in currentMenu[section].items) {
                const currentItem = currentMenu[section].items[itemKey]
                const initialItem =
                  initialMenu?.[section]?.items?.[itemKey] || {}
                const currentOtherItem =
                  currentOtherMenu?.[section]?.items?.[itemKey] || {}
                const initialOtherItem =
                  initialOtherMenu?.[section]?.items?.[itemKey] || {}

                for (const field in currentItem) {
                  if (
                    currentItem[field] !== initialItem[field] &&
                    (currentOtherItem[field] === undefined ||
                      currentOtherItem[field] === initialOtherItem[field])
                  ) {
                    editedMenuFields.push(`${section}.${itemKey}.${field}`)
                  }
                }
              }
            }
          }
        }
      }

      if (editedMenuFields.length > 0 && !confirmMissing) {
        setConfirmMissing({ edited: editedMenuFields })
        setIsSaving(false)
        return
      }
    } else if (
      (!isMenuPage && !isListinoPage) ||
      currentModule === "translation"
    ) {
      // Regular pages check
      const editedKeys = Object.keys(flatTranslations).filter(
        (key) => flatTranslations[key] !== initialFlatTranslations[key]
      )

      const missingOtherKeys = editedKeys.filter(
        (key) =>
          !otherFlatTranslations[key] ||
          otherFlatTranslations[key] === "" ||
          otherFlatTranslations[key] === initialOtherFlatTranslations[key]
      )

      if (missingOtherKeys.length > 0 && !confirmMissing) {
        setConfirmMissing({ edited: editedKeys, missing: missingOtherKeys })
        setIsSaving(false)
        return
      }
    }

    try {
      const isMenuOrListinoPage = [
        "menuCarta",
        "wineList",
        "menuAperitivo",
        "listino",
      ].includes(currentPage)

      // For the translation module, use flattened translations if we're not on a menu or listino page
      // Otherwise, use the full nested translations
      const dataToSaveCurrent =
        isMenuOrListinoPage || currentModule !== "translation"
          ? translations
          : unflattenTranslations(flatTranslations)

      const dataToSaveOther =
        isMenuOrListinoPage || currentModule !== "translation"
          ? otherTranslations
          : unflattenTranslations(otherFlatTranslations)

      const resultCurrent = await saveTranslations(
        i18n.language,
        dataToSaveCurrent
      )

      const secondaryLang = getSecondaryLanguage()
      const resultOther = await saveTranslations(secondaryLang, dataToSaveOther)

      setMessage({
        type: "success",
        text: `${
          resultCurrent.message || "Translations saved for current language!"
        } 
               ${
                 resultOther.message ||
                 "Translations saved for secondary language!"
               }`,
      })

      i18n.addResourceBundle(
        i18n.language,
        "global",
        dataToSaveCurrent,
        true,
        true
      )

      i18n.addResourceBundle(
        secondaryLang,
        "global",
        dataToSaveOther,
        true,
        true
      )

      closeEditor()
      setTimeout(() => {
        window.location.reload()
      }, 500)
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setIsSaving(false)
      setConfirmMissing(null)
    }
  }

  const closeEditor = () => {
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search
    )
    setIsEditMode(false)
  }

  // --- RENDERING FUNCTIONS ---

  // Render the regular translation editor
  const renderRegularEditor = () => {
    const currentPage = getCurrentPage()
    const filteredTranslations = Object.keys(flatTranslations)
      .filter((key) => {
        const lowerKey = key.toLowerCase()
        const lowerPage = currentPage.toLowerCase()
        if (lowerKey.startsWith(`${lowerPage}.`)) return true
        if (lowerKey === lowerPage) return true
        if (lowerKey.includes(`.${lowerPage}.`)) return true
        if (lowerKey.startsWith(`form.${lowerPage}`)) return true
        return false
      })
      .reduce((obj, key) => {
        obj[key] = flatTranslations[key]
        return obj
      }, {})

    return (
      <div className="space-y-4">
        {Object.keys(filteredTranslations).length === 0 ? (
          <div className="text-center p-4 bg-gray-50 rounded">
            No translation strings found for this page.
          </div>
        ) : (
          Object.entries(filteredTranslations).map(([key, value]) => {
            const useTextarea =
              key.toLowerCase().includes("description") ||
              key.toLowerCase().includes("desc") ||
              key.toLowerCase().includes("keywords") ||
              (value && value.length > 50)

            return (
              <div
                key={key}
                className="mb-4 border border-gray-200 p-4 rounded-lg bg-white shadow-sm"
              >
                <label className="block font-bold mb-2 text-blue-800">
                  {key}
                </label>
                {key === "home.popup.enabled" ? (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={value?.toLowerCase() === "true"}
                      onChange={(e) =>
                        handleRegularChange(
                          key,
                          e.target.checked ? "true" : "false"
                        )
                      }
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2 text-gray-700">Enabled</span>
                  </div>
                ) : useTextarea ? (
                  <textarea
                    rows="4"
                    value={value || ""}
                    onChange={(e) => handleRegularChange(key, e.target.value)}
                    className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <input
                    type="text"
                    value={value || ""}
                    onChange={(e) => handleRegularChange(key, e.target.value)}
                    className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                )}
                <button
                  onClick={() => toggleOtherKey(key)}
                  className="mt-3 text-sm text-blue-600 hover:text-blue-800 flex items-center"
                >
                  {toggleOtherKeys[key] ? (
                    <>
                      <FaTimes className="mr-1" /> Hide
                    </>
                  ) : (
                    <>
                      <FaEdit className="mr-1" /> Show
                    </>
                  )}{" "}
                  {getSecondaryLanguage().toUpperCase()}
                </button>
                {toggleOtherKeys[key] && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <label className="block font-bold mb-2 text-red-700">
                      {key} ({getSecondaryLanguage().toUpperCase()})
                    </label>
                    {key === "home.popup.enabled" ? (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={
                            otherFlatTranslations[key]?.toLowerCase() === "true"
                          }
                          onChange={(e) =>
                            handleOtherRegularChange(
                              key,
                              e.target.checked ? "true" : "false"
                            )
                          }
                          className="form-checkbox h-5 w-5 text-red-600"
                        />
                        <span className="ml-2 text-gray-700">Enabled</span>
                      </div>
                    ) : useTextarea ? (
                      <textarea
                        rows="4"
                        value={otherFlatTranslations[key] || ""}
                        onChange={(e) =>
                          handleOtherRegularChange(key, e.target.value)
                        }
                        className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    ) : (
                      <input
                        type="text"
                        value={otherFlatTranslations[key] || ""}
                        onChange={(e) =>
                          handleOtherRegularChange(key, e.target.value)
                        }
                        className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    )}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    )
  }

  const renderConfirmMissingModal = () => {
    if (!confirmMissing) return null
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-3xl mx-4 w-full overflow-auto max-h-[90vh] shadow-xl">
          <h3 className="text-xl font-bold mb-4 flex items-center text-amber-600">
            <FaExclamationCircle className="text-amber-500 mr-2" />
            Warning
          </h3>
          {["menuCarta", "wineList", "menuAperitivo", "listino"].includes(
            getCurrentPage()
          ) ? (
            <>
              <p className="mb-2">
                You've modified the following fields in{" "}
                <span className="font-bold text-blue-700">
                  {i18n.language.toUpperCase()}
                </span>{" "}
                for the menu, but haven't updated the{" "}
                <span className="font-bold text-red-700">
                  {getSecondaryLanguage().toUpperCase()}
                </span>{" "}
                version:
              </p>
              <ul className="mb-4 list-disc list-inside bg-amber-50 p-3 rounded-md">
                {confirmMissing.edited.map((key) => (
                  <li key={key} className="my-1">
                    <strong className="text-gray-800">{key}</strong>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <p className="mb-2">
                You've modified the following strings in{" "}
                <span className="font-bold text-blue-700">
                  {i18n.language.toUpperCase()}
                </span>
                :
              </p>
              <ul className="mb-4 list-disc list-inside bg-blue-50 p-3 rounded-md max-h-[30vh] overflow-y-auto">
                {confirmMissing.edited.map((key) => (
                  <li key={key} className="my-1">
                    <strong className="text-gray-800">{key}</strong>:{" "}
                    {flatTranslations[key]}
                  </li>
                ))}
              </ul>
              <p className="mb-2">
                The following keys have not been updated for{" "}
                <span className="font-bold text-red-700">
                  {getSecondaryLanguage().toUpperCase()}
                </span>
                :
              </p>
              <ul className="mb-4 list-disc list-inside bg-red-50 p-3 rounded-md max-h-[30vh] overflow-y-auto">
                {confirmMissing.missing.map((key) => (
                  <li key={key} className="my-1">
                    <strong className="text-gray-800">{key}</strong>
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="mb-4 font-medium">
            Do you want to proceed with saving anyway?
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setConfirmMissing(null)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors duration-200 flex items-center"
            >
              <FaArrowLeft className="mr-2" /> Go Back
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center"
            >
              <FaSave className="mr-2" /> Save Anyway
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!isEditMode || !ready) return null
  const currentPage = getCurrentPage()
  const currentLang = i18n.language
  const secondaryLang = getSecondaryLanguage()

  return (
    <div
      className="fixed inset-0 flex justify-center items-center z-50"
      style={{
        backdropFilter: "blur(5px)",
        backgroundColor: "rgba(0,0,0,0.4)",
      }}
    >
      <div className="bg-white/95 rounded-lg w-11/12 h-[95vh] flex flex-col shadow-2xl border border-gray-200">
        <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gradient-to-r from-blue-700 to-red-700 text-white rounded-t-lg">
          <h2 className="text-2xl font-bold flex items-center">
            <img
              src="/images/eagle-icon-white.png"
              alt="Eagle icon"
              className="w-8 h-8 mr-3"
              onError={(e) => (e.target.style.display = "none")}
            />
            Translation Editor{" "}
            {currentModule !== "translation"
              ? `- ${
                  currentModule.charAt(0).toUpperCase() + currentModule.slice(1)
                }`
              : ""}
          </h2>
          <button
            onClick={closeEditor}
            className="text-white hover:text-gray-200 p-2 rounded-full hover:bg-white/10 transition-colors duration-200"
            title="Close editor"
          >
            <IoMdClose size={24} />
          </button>
        </div>

        <div className="p-4 md:p-6 flex-1 overflow-y-auto bg-gray-50">
          {/* Status messages */}
          {message && (
            <div
              className={`mb-4 p-4 rounded-lg flex items-center shadow-md ${
                message.type === "success"
                  ? "bg-green-100 text-green-800 border-l-4 border-green-500"
                  : message.type === "loading"
                  ? "bg-blue-100 text-blue-800 border-l-4 border-blue-500"
                  : "bg-red-100 text-red-800 border-l-4 border-red-500"
              }`}
            >
              {message.type === "success" ? (
                <svg
                  className="w-6 h-6 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : message.type === "loading" ? (
                <svg
                  className="animate-spin h-6 w-6 mr-3 flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <svg
                  className="w-6 h-6 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              <div className="flex-1">{message.text}</div>
            </div>
          )}

          {/* Module Selector */}
          <ModuleSelector
            currentModule={currentModule}
            onModuleChange={(module) => {
              setCurrentModule(module)
              clearSubmoduleAction()
            }}
            currentPage={currentPage}
          />

          {/* Module Content */}
          <div className="mt-4 bg-white p-5 rounded-lg shadow-sm border border-gray-200">
            {currentModule === "translation" && renderRegularEditor()}

            {currentModule === "products" && (
              <ProductManager
                currentTranslations={translations}
                otherTranslations={otherTranslations}
                currentLang={currentLang}
                secondaryLang={secondaryLang}
                onUpdateTranslations={handleUpdateTranslations}
                registerSubmoduleAction={registerSubmoduleAction}
                clearSubmoduleAction={clearSubmoduleAction}
              />
            )}

            {currentModule === "menu" && (
              <MenuManager
                currentTranslations={translations}
                otherTranslations={otherTranslations}
                currentLang={currentLang}
                secondaryLang={secondaryLang}
                onUpdateTranslations={handleUpdateTranslations}
                page={currentPage}
                onSectionChange={(section) => {
                  // Function to handle section change if needed
                  console.log("Change to section:", section)
                }}
                onModuleChange={(module) => {
                  // Function to handle module change if needed
                  setCurrentModule(module)
                }}
                registerSubmoduleAction={registerSubmoduleAction}
                clearSubmoduleAction={clearSubmoduleAction}
              />
            )}

            {currentModule === "listino" && (
              <ListinoManager
                currentTranslations={translations}
                otherTranslations={otherTranslations}
                currentLang={currentLang}
                secondaryLang={secondaryLang}
                onUpdateTranslations={handleUpdateTranslations}
                registerSubmoduleAction={registerSubmoduleAction}
                clearSubmoduleAction={clearSubmoduleAction}
              />
            )}

            {currentModule === "images" && (
              <ImageManager
                onSelectImage={(path) => console.log("Selected image:", path)}
              />
            )}
          </div>

          {renderConfirmMissingModal()}
        </div>

        {/* Contextual Footer */}
        {showSubmoduleFooter && submoduleAction ? (
          <div className="p-4 border-t border-gray-200 flex justify-between items-center bg-blue-50">
            <div className="flex items-center">
              <div className="hidden md:flex items-center mr-2 text-blue-800">
                <FaEdit className="mr-2" />
                <span className="font-medium">Editing: {submoduleName}</span>
              </div>
              <button
                onClick={() => clearSubmoduleAction()}
                className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors flex items-center"
              >
                <FaArrowLeft className="mr-1 text-sm" /> Cancel
              </button>
            </div>
            <button
              onClick={submoduleAction}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center shadow-md"
            >
              <FaSave className="mr-2" /> Save Changes
            </button>
          </div>
        ) : (
          <div className="p-4 border-t border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-700 to-red-700 rounded-b-lg">
            <div className="text-white text-sm md:text-base">
              Editing: <span className="font-bold">{getCurrentPage()}</span> (
              {currentLang.toUpperCase()} → {secondaryLang.toUpperCase()})
            </div>
            <div className="flex space-x-3">
              <button
                onClick={closeEditor}
                className="px-4 py-2 bg-white/10 text-white rounded-md hover:bg-white/20 transition-colors flex items-center border border-white/30"
              >
                <FaTimes className="mr-2" /> Close
              </button>
              <button
                onClick={handleSave}
                className={`px-4 py-2 bg-white text-blue-800 font-medium rounded-md hover:bg-blue-50 transition-colors flex items-center shadow-md ${
                  isSaving ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={isSaving}
              >
                <FaSave className="mr-2" />
                {isSaving ? "Saving..." : "Save All"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TranslationEditor
